import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import type { Extension } from "remirror";
import "remirror/styles/all.css";

import { openDownloadLink } from "simplydo/core/storage";
import type { UserMe } from "simplydo/interfaces/types/userTypes";

import api from "api";

import { useMe } from "../../hooks";
import EditorWrapper from "./editor";
import type { RemirrorInnerEditorProps } from "./editorTypes";
import createExtension from "./extensions";
import { createMentionNodeViewExtension } from "./extensions/custom/mentionNodeView";
import { DefaultPreset } from "./extensions/default";
import type { Preset } from "./types";
import ReactTextEditor from "./view";

export type RemirrorProps = {
  options?: Preset;
  children?: React.ReactNode;
  disableBorder?: boolean;
  autoFocus?: boolean;

  settings?: {
    uploadOptions?: { forType: string; forId: string };
    uploadFile?: (
      file: File,
      options: { forType: string; forId: string },
    ) => Promise<string>;
    onExternalLink?: (url: string) => void;
    onFileClick?: (fileId: string) => void;
    fullMentionLinks?: boolean;
    renderMention?:
      | ((args: {
          kind: string;
          id: string;
          label?: string;
          attrs: Record<string, unknown>;
        }) => React.ReactNode)
      | undefined;
  };
  historyEnabled?: boolean;
} & Omit<RemirrorInnerEditorProps, "children" | "extensions">;

type ExtensionConfig = Record<string, unknown> & {
  uploadFile?: (
    file: File,
    options: { forType: string; forId: string },
  ) => Promise<string>;
  uploadHandler?: (
    files: Array<{ file: File; progress: (value: number) => void }>,
  ) => Array<() => Promise<{ src: string; name: string; fileName: string }>>;
  formatMention?: (item: Record<string, unknown>) => Record<string, unknown>;
  onClick?: (src: string, props: { name: string }) => boolean | void;
};

const localRegex = new RegExp(
  `((^| )https://(([a-z]{1,15}.)?simplydo.co.uk(/| |$)))`,
  "gi",
);
const devRegex = new RegExp(
  `((^| )(http://|https://)(([a-z]{1,15}.)?localhost(/| |$)))`,
  "gi",
);

const checkLocalLink = (href: string) => {
  if (href.startsWith("/")) {
    return { isLocal: true, path: href };
  }

  const localMatch = href.match(localRegex);
  if (localMatch) {
    return { isLocal: true, path: `/${href.replace(localRegex, "")}` };
  }
  const devMatch = href.match(devRegex);
  if (devMatch) {
    return { isLocal: true, path: `/${href.replace(devRegex, "")}` };
  }
  return { isLocal: false, path: href };
};

const getExtensions = (
  options: Preset = DefaultPreset,
  settings: RemirrorProps["settings"] = {},
  readOnly: boolean,
  navigate: (path: string) => void,
  user: UserMe | undefined,
) => {
  const order: string[] = [];

  const combinedOptions = options.reduce<
    Record<string, Array<ExtensionConfig>>
  >((p, { name, config = {} }) => {
    const typedConfig = config as ExtensionConfig;
    if (!order.includes(name)) {
      order.push(name);
    }

    if ("uploadFile" in typedConfig && settings.uploadOptions) {
      const uploadOptions = settings.uploadOptions;
      typedConfig.uploadHandler = (files) => {
        const uploadFile = typedConfig.uploadFile;
        if (!uploadFile) {
          return [];
        }
        return files.map(
          ({ file, progress }, i) =>
            () =>
              uploadFile(file, uploadOptions).then((src) => {
                progress((i + 1) / files.length);
                return { src, name: file.name, fileName: file.name };
              }),
        );
      };
    }

    if (name === "image") {
      typedConfig.enableResizing = !readOnly;
    }

    if (name === "mention" && settings.fullMentionLinks) {
      const relativeFormatMention =
        typedConfig.formatMention ?? ((item) => item);
      typedConfig.formatMention = (item) => {
        const formatted = relativeFormatMention(item);
        return {
          ...formatted,
          href: window.location.origin + String(formatted.href ?? ""),
        };
      };
    }

    const prev = p[name];
    if (!prev) {
      p[name] = [typedConfig];
    } else {
      p[name] = [...prev, typedConfig];
    }
    return p;
  }, {});

  const initialisedExtensions = order.map((name) => {
    if (name === "file") {
      const configs = combinedOptions[name];
      const firstConfig = configs?.[0] ?? {};
      combinedOptions[name] = [
        {
          ...firstConfig,
          onClick: (src: string, props: { name: string }) => {
            if (!user) {
              firstConfig.onClick?.(src, props);
              return false;
            }
            const urlData = new URL(src);
            const parameters = urlData.searchParams;
            api.uploads.getFileDownloadCookies(
              {
                forType: parameters.get("forType") ?? undefined,
                forId: parameters.get("forId") ?? undefined,
                entityType: parameters.get("entityType") ?? undefined,
                entityId: parameters.get("entityId") ?? undefined,
                identifier: parameters.get("identifier") ?? undefined,
              },
              ({ url }) => {
                openDownloadLink(url, props.name);
              },
              (error) => {
                console.error("Error getting file download cookies: ", error);
              },
            );
          },
        },
      ];
    }

    const extension = createExtension(name, combinedOptions[name]);

    if (name === "link") {
      extension.addHandler(
        "onClick",
        (_event: unknown, data: { href: string }) => {
          if (!readOnly) {
            return false;
          }
          if (data.href.startsWith("#")) {
            const anchor = document.getElementById(data.href.slice(1));
            if (anchor) {
              anchor.scrollIntoView({ behavior: "smooth" });
            }
            return true;
          }

          const localLink = checkLocalLink(data.href);
          if (localLink.isLocal) {
            navigate(localLink.path);
          } else {
            if (!data.href.startsWith("mailto:") && settings.onExternalLink) {
              settings.onExternalLink(data.href);
            } else {
              window.open(data.href, "_blank");
            }
          }

          return true;
        },
      );
    }

    return extension;
  });

  if (readOnly) {
    initialisedExtensions.unshift(
      createMentionNodeViewExtension({ renderMention: settings.renderMention }),
    );
  }

  return { initialisedExtensions };
};

export const RemirrorEditor = ({
  value,
  onChange,
  onChangeJson,
  onFocus,
  onBlur,
  emptyReturnValue,
  options = DefaultPreset,
  readOnly = false,
  placeholder = null,
  disableBorder,
  settings,
  editorRef,
  markdown,
  children,
  autoFocus,
}: RemirrorProps) => {
  const navigate = useNavigate();
  const user = useMe();
  const [extensions, setExtensions] = useState(() =>
    getExtensions(options, settings, readOnly, navigate, user),
  );
  const [_enabledExtensions, setEnabledExtensions] = useState<string[]>([]);
  const { initialisedExtensions } = extensions;

  useEffect(() => {
    setExtensions(getExtensions(options, settings, readOnly, navigate, user));
    setEnabledExtensions(options.map((o) => o.name));
  }, [options, settings, readOnly, navigate, user]);

  const focusHandler = useCallback(
    (event: unknown) => {
      onFocus?.(event);
    },
    [onFocus],
  );

  const blurHandler = useCallback(
    (html: string, textValue: string) => {
      onBlur?.(html, textValue);
    },
    [onBlur],
  );

  return (
    <EditorWrapper
      placeholder={placeholder}
      readOnly={readOnly}
      value={value}
      onChange={onChange}
      onChangeJson={onChangeJson}
      onBlur={blurHandler}
      onFocus={focusHandler}
      emptyReturnValue={emptyReturnValue}
      extensions={initialisedExtensions as Extension[]}
      editorRef={editorRef}
      autoFocus={autoFocus}
      markdown={markdown}
    >
      {children}
      <ReactTextEditor disableBorder={disableBorder} />
    </EditorWrapper>
  );
};
